import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { MODULE_LIST, MODULES } from "@/config/modules";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter,
} from "@/components/ui/alert-dialog";
import NotificationBell from "@/components/NotificationBell";
import ThemeToggle from "@/components/ThemeToggle";

const IDLE_WARN_MS = 40 * 60 * 1000; // show warning after 40 min inactivity
const IDLE_GRACE_MS = 60 * 1000;     // then log out 1 min later

const QUICK_ADD = ["contacts", "notes", "followups", "documents", "vendors", "rates", "expenses", "projects"];

const PAYMENT_KEYS = ["payment_ledger", "expenses"];

function buildSections(can, isAdmin) {
  const mods = MODULE_LIST.filter((m) => can(m.key, "view"));
  const toItem = (m) => ({ key: m.key, name: m.name, icon: m.icon, path: m.customPath || `/m/${m.key}` });
  const mainMods = mods.filter((m) => !PAYMENT_KEYS.includes(m.key));
  const payMods = mods.filter((m) => PAYMENT_KEYS.includes(m.key));

  const sections = [
    { title: null, items: [{ key: "dashboard", name: "Dashboard", icon: "LayoutDashboard", path: "/" },
      ...(isAdmin ? [{ key: "overnight_report", name: "Overnight Auto-Exit Report", icon: "MoonStar", path: "/overnight-report" }] : []),
      ...mainMods.map(toItem)] },
  ];

  const gpi = [];
  if (can("gate_pass", "view")) gpi.push({ key: "gate_pass", name: "Gate Pass", icon: "ClipboardCheck", path: "/gate-pass" });
  if (can("security", "view")) gpi.push({ key: "security", name: "Security Entry & Exit", icon: "ShieldCheck", path: "/crm/projects/gpi/security" });
  if (can("safety", "view")) gpi.push({ key: "safety", name: "Morning Safety Check", icon: "ShieldAlert", path: "/crm/projects/gpi/safety" });
  if (gpi.length) sections.push({ title: "Project GPI", items: gpi });

  if (payMods.length || can("finance", "view")) {
    const payItems = payMods.map(toItem);
    if (can("finance", "view")) payItems.push({ key: "finance", name: "Money Receipt / Paid Memo", icon: "ReceiptText", path: "/finance" });
    if (isAdmin) payItems.push({ key: "party_ledger", name: "Party Ledger", icon: "BookUser", path: "/party-ledger" });
    sections.push({ title: "Payments", items: payItems });
  }

  if (isAdmin) {
    sections.push({ title: "Admin Control", items: [
      { key: "users", name: "CRM Users", icon: "UsersRound", path: "/users" },
      { key: "roles", name: "Roles & Permissions", icon: "ShieldCheck", path: "/roles" },
      { key: "activity", name: "Activity Log", icon: "History", path: "/activity" },
      { key: "settings", name: "Settings", icon: "Settings", path: "/settings" },
    ] });
  }
  return sections;
}

const flatNav = (sections) => sections.flatMap((s) => s.items);

function NavList({ sections, onNavigate }) {
  const location = useLocation();
  return (
    <nav className="flex flex-col gap-1 px-3 pb-3">
      {sections.map((sec, si) => (
        <div key={si} className={si > 0 ? "mt-4" : ""}>
          {sec.title && <div className="px-3 mb-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{sec.title}</div>}
          <div className="flex flex-col gap-1">
            {sec.items.map((item) => {
              const Icon = Icons[item.icon] || Icons.Circle;
              const active = location.pathname === item.path;
              return (
                <Link key={item.key} to={item.path} onClick={onNavigate} data-testid={`nav-${item.key}`}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    active ? "bg-primary text-primary-foreground" : "text-slate-300 hover:bg-white/10 hover:text-white"
                  }`}>
                  <Icon className="h-[18px] w-[18px]" />
                  {item.name}
                </Link>
              );
            })}
          </div>
        </div>
      ))}
    </nav>
  );
}

function Brand() {
  return (
    <div className="flex items-center gap-2.5 px-5 py-5">
      <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center font-display font-extrabold text-primary-foreground">B</div>
      <div className="leading-tight">
        <div className="font-display font-bold text-white text-[15px]">Project GPI</div>
        <div className="text-[11px] text-slate-400 tracking-wide">INFORMATION SYSTEM</div>
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout, can, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [q, setQ] = useState("");
  const sections = buildSections(can, isAdmin);
  const nav = flatNav(sections);
  const quickAdd = QUICK_ADD.filter((k) => can(k, "create"));

  // Auto-logout on inactivity: warn at 40 min, log out 1 min later.
  const [idleWarn, setIdleWarn] = useState(false);
  const [idleCountdown, setIdleCountdown] = useState(60);
  const idleWarnRef = useRef(false);
  const warnTimer = useRef(null);
  const logoutTimer = useRef(null);
  const countdownTimer = useRef(null);

  const clearIdleTimers = useCallback(() => {
    clearTimeout(warnTimer.current);
    clearTimeout(logoutTimer.current);
    clearInterval(countdownTimer.current);
  }, []);

  const startIdleTimers = useCallback(() => {
    clearIdleTimers();
    warnTimer.current = setTimeout(() => {
      idleWarnRef.current = true;
      setIdleCountdown(Math.round(IDLE_GRACE_MS / 1000));
      setIdleWarn(true);
      countdownTimer.current = setInterval(() => {
        setIdleCountdown((s) => (s > 0 ? s - 1 : 0));
      }, 1000);
      logoutTimer.current = setTimeout(() => {
        clearIdleTimers();
        logout();
      }, IDLE_GRACE_MS);
    }, IDLE_WARN_MS);
  }, [clearIdleTimers, logout]);

  const stayLoggedIn = useCallback(() => {
    idleWarnRef.current = false;
    setIdleWarn(false);
    startIdleTimers();
  }, [startIdleTimers]);

  useEffect(() => {
    startIdleTimers();
    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "click"];
    const onActivity = () => { if (!idleWarnRef.current) startIdleTimers(); };
    events.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));
    return () => {
      events.forEach((ev) => window.removeEventListener(ev, onActivity));
      clearIdleTimers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doSearch = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  const initials = (user?.name || "U").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col bg-slate-900 border-r border-slate-800 z-30">
        <Brand />
        <div className="flex-1 overflow-y-auto py-2"><NavList sections={sections} /></div>
        <div className="p-3 border-t border-slate-800">
          <div className="flex items-center gap-2 px-2 py-1.5">
            <Avatar className="h-8 w-8"><AvatarFallback className="bg-primary/20 text-primary text-xs">{initials}</AvatarFallback></Avatar>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-white truncate">{user?.name}</div>
              <div className="text-[11px] text-slate-400 capitalize">{user?.role?.replace("_", " ")}</div>
            </div>
            <Button size="icon" variant="ghost" className="text-slate-400 hover:text-white h-8 w-8" onClick={logout} data-testid="logout-btn">
              <Icons.LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="lg:pl-64">
        {/* Top header */}
        <header className="sticky top-0 z-20 bg-background/80 backdrop-blur-md border-b border-border">
          <div className="flex items-center gap-3 px-4 sm:px-6 h-16">
            {/* Mobile menu */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button size="icon" variant="ghost" className="lg:hidden" data-testid="mobile-menu-btn">
                  <Icons.Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 h-full bg-slate-900 border-slate-800 p-0 flex flex-col">
                <Brand />
                <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain py-2" style={{ WebkitOverflowScrolling: "touch" }} data-testid="mobile-nav-scroll">
                  <NavList sections={sections} onNavigate={() => setMobileOpen(false)} />
                </div>
                <div className="shrink-0 p-3 border-t border-slate-800">
                  <div className="flex items-center gap-2 px-2 py-1.5">
                    <Avatar className="h-8 w-8"><AvatarFallback className="bg-primary/20 text-primary text-xs">{initials}</AvatarFallback></Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white truncate">{user?.name}</div>
                      <div className="text-[11px] text-slate-400 capitalize">{user?.role?.replace("_", " ")}</div>
                    </div>
                    <Button variant="ghost" className="text-slate-300 hover:text-white hover:bg-white/10 gap-2 h-9" onClick={() => { setMobileOpen(false); logout(); }} data-testid="mobile-logout-btn">
                      <Icons.LogOut className="h-4 w-4" /> Logout
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>

            <form onSubmit={doSearch} className="flex-1 max-w-2xl relative">
              <Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search everything — contacts, vendors, documents, notes…"
                className="pl-9 bg-secondary/60 border-transparent focus-visible:bg-card" data-testid="global-search-input" />
            </form>

            {/* Add New */}
            <ThemeToggle />
            <NotificationBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="gap-1.5 active:scale-95 transition-transform" data-testid="add-new-btn">
                  <Icons.Plus className="h-4 w-4" /><span className="hidden sm:inline">Add New</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel>Quick Add</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {quickAdd.map((k) => {
                  const m = MODULES[k];
                  const Icon = Icons[m.icon] || Icons.Circle;
                  return (
                    <DropdownMenuItem key={k} onClick={() => navigate(`/m/${k}?add=1`)} data-testid={`quick-add-${k}`}>
                      <Icon className="h-4 w-4 mr-2" />{m.singular}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="px-4 sm:px-6 py-6 pb-24 lg:pb-6 max-w-[1400px]">{children}</main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-20 bg-card/95 backdrop-blur-md border-t border-border flex justify-around py-1.5">
        {["dashboard", "contacts", "followups", "activity"].map((k) => nav.find((n) => n.key === k)).filter(Boolean).map((item) => {
          const Icon = Icons[item.icon] || Icons.Circle;
          return (
            <Link key={item.key} to={item.path} data-testid={`bottomnav-${item.key}`}
              className="flex flex-col items-center gap-0.5 px-3 py-1 text-[10px] text-muted-foreground">
              <Icon className="h-5 w-5" />{item.name}
            </Link>
          );
        })}
      </nav>

      {/* Inactivity warning */}
      <AlertDialog open={idleWarn}>
        <AlertDialogContent data-testid="idle-warning-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Your session is about to expire</AlertDialogTitle>
            <AlertDialogDescription>
              You've been inactive for a while. For your security, you'll be logged out in{" "}
              <span className="font-semibold text-foreground" data-testid="idle-countdown">{idleCountdown}</span> second{idleCountdown === 1 ? "" : "s"}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => { setIdleWarn(false); clearIdleTimers(); logout(); }} data-testid="idle-logout-now-btn">
              Log out now
            </Button>
            <Button onClick={stayLoggedIn} data-testid="idle-stay-btn">
              Stay logged in
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
